import * as React from 'react';
import * as classNames from 'classnames';
import {AutoSizer, List, CellMeasurer, CellMeasurerCache} from 'react-virtualized';
import {ConsoleLine} from './line';
import {ConsoleRunning} from './running';
import {IConsoleMessage, ITests} from '../../redux/modules/console/reducer';
import {RUNNING} from '../../redux/modules/console/constants';
import {ConsoleTestsWrapper as TestsWrapper} from './tests-wrapper';
import {ConsoleReadyToRun as ReadyToRun} from './ready-to-run';
import {ConsoleScrollHint as ScrollHint} from './consoleScrollHint';

import './style.sass';

interface IAutoSizer {
  width: number;
  height: number;
}

interface IMessage {
  index: number;
  isScrolling: boolean;
  key: string;
  style: any;
  parent?: any;
}

export interface IConsoleProps {
  messages: Array<IConsoleMessage>;
  toggle: () => void;
  running: boolean;
  ackConsoleScroll: () => void;
  showConsoleScrollHint: boolean;
  tests: ITests;
  hasTests: boolean;
  hasErrorCompilation: boolean;
  runTests: boolean;
}

const ROW_HEIGHT = 20;

export class Console extends React.Component<IConsoleProps, any> {
  private cache: CellMeasurerCache;
  private list: List;

  constructor(props: IConsoleProps) {
    super(props);

    this.cache = new CellMeasurerCache({
      fixedWidth: true,
      minHeight: ROW_HEIGHT,
    });
  }

  public componentDidUpdate(prevProps: IConsoleProps): void {
    this.redrawList(prevProps);
  }

  public render(): React.ReactElement<any> {
    const {
      hasTests,
      messages,
      tests,
      toggle,
      running,
      hasErrorCompilation,
      runTests,
      showConsoleScrollHint, ackConsoleScroll,
    } = this.props;

    const highlightConsoleHeader = !running && !hasErrorCompilation && hasTests;

    return (
      <div className={'console-component'}>
        <div className={'console-component--header'}>
          <ul className={'console-component-tab-list'}>
            <li className={classNames('console-component-tab', {
              'console-component-tab-green': highlightConsoleHeader && tests.failed === 0 && tests.passed > 0,
              'console-component-tab-red': highlightConsoleHeader && tests.failed > 0,
            })}>Console</li>
          </ul>
          <span className={'console-component--header-toggle'} onClick={toggle}>
            <span className={'fa fa-close icon icon--animated'} />
          </span>
        </div>

        {!messages.length && <ReadyToRun />}

        {runTests && hasTests && !hasErrorCompilation
          ? <TestsWrapper
              tests={tests}
              consoleHintShown={showConsoleScrollHint && !running}
              ackConsoleScroll={ackConsoleScroll}
            />
          : <div className={'console-component--container'}>
              <AutoSizer>
                {(options: IAutoSizer) => this.messages(options)}
              </AutoSizer>
            </div>
        }
      </div>
    );
  }

  private messages({width, height}: IAutoSizer): React.ReactElement<any> {
    const {messages, ackConsoleScroll} = this.props;
    const messagesLen = messages.length;
    const consoleHintShown = this.getConsoleHintVisibility(height);
    const cache: CellMeasurerCache = this.cache;

    return (
      <div>
        <ScrollHint
          visible={consoleHintShown}
          onHide={ackConsoleScroll}
        />

        <List
          ref={this.setListRef}
          width={width}
          height={height}
          rowCount={messagesLen}
          rowRenderer={this.message}
          className={'console-component--output'}
          deferredMeasurementCache={cache}
          rowHeight={cache.rowHeight}
        />
      </div>
    );
  }

  private message = ({index, style, parent, key}: IMessage): React.ReactElement<any> => {
    const message: IConsoleMessage = this.props.messages[index];

    return message.data === RUNNING
      ? <ConsoleRunning key={message.id} style={style} />
      : <CellMeasurer
          cache={this.cache}
          columnIndex={0}
          key={key}
          parent={parent}
          rowIndex={index}
        >
          <ConsoleLine message={message} style={style} />
        </CellMeasurer>;
  };

  private getConsoleHintVisibility = (height): boolean => {
    const {messages, running, showConsoleScrollHint} = this.props;

    return showConsoleScrollHint
      && !running
      && height < Math.ceil(messages.length * ROW_HEIGHT);
  };

  private setListRef = ref => {
    this.list = ref;
  };

  private redrawList = (prevProps: IConsoleProps) => {
    const prevMessagesLen = prevProps.messages.length;
    const nextMessagesLen = this.props.messages.length;

    if (!this.list || nextMessagesLen === 0) {
      return;
    }

    if (prevMessagesLen === nextMessagesLen) {
      // https://github.com/bvaughn/react-virtualized#pure-components
      this.list.forceUpdateGrid();
    }

    this.cache.clearAll();
    this.list.scrollToRow(nextMessagesLen - 1);
  }
}
